import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../../../constants';
import { APTITUDE_RANKS, getAptitudeSelectStyle } from '../../../utils/aptitudeRanks';
import { useAuth } from '../../../context/AuthContext';

const CharacterAptitudes = ({ characterId, cardId, initialAptitudes, onSave, isNew = false }) => {
    const { t } = useTranslation();
    const { token } = useAuth();
    const [formData, setFormData] = useState({
        turf: 'E',
        dirt: 'E',
        short_distance: 'E',
        mile: 'E',
        medium_distance: 'E',
        long_distance: 'E',
        runner: 'E',
        leader: 'E',
        betweener: 'E',
        chaser: 'E'
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);


    useEffect(() => {
        if (initialAptitudes) {
            setFormData({
                turf: initialAptitudes.turf || 'E',
                dirt: initialAptitudes.dirt || 'E',
                short_distance: initialAptitudes.short_distance || 'E',
                mile: initialAptitudes.mile || 'E',
                medium_distance: initialAptitudes.medium_distance || 'E',
                long_distance: initialAptitudes.long_distance || 'E',
                runner: initialAptitudes.runner || 'E',
                leader: initialAptitudes.leader || 'E',
                betweener: initialAptitudes.betweener || 'E',
                chaser: initialAptitudes.chaser || 'E'
            });
        } else {
            // Reset if no aptitudes provided (e.g. switching to a new card without data)
            setFormData({
                turf: 'E',
                dirt: 'E',
                short_distance: 'E',
                mile: 'E',
                medium_distance: 'E',
                long_distance: 'E',
                runner: 'E',
                leader: 'E',
                betweener: 'E',
                chaser: 'E'
            });
        }
    }, [initialAptitudes, cardId]); // Reset when cardId changes

    const handleChange = (name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (success) setSuccess(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isNew) {
            onSave(formData);
            return;
        }

        if (!cardId) {
            setError(t('CharacterAptitudes.error_no_card'));
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(false);

        try {
            // Use cardId for the endpoint
            const response = await fetch(`${API_BASE_URL}/aptitudes/${cardId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                // Include Id_Personaje for consistency/validity checks
                body: JSON.stringify({
                    ...formData,
                    Id_Personaje: characterId
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || t('CharacterAptitudes.error_save'));
            }

            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);

            // Notify parent to refresh data
            onSave();

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const AptitudeSelect = ({ label, name, value }) => (
        <div className="flex flex-col">
            <label className="text-xs font-semibold text-gray-500 mb-1 uppercase">{label}</label>
            <select
                value={value}
                onChange={(e) => handleChange(name, e.target.value)}
                disabled={!cardId && !isNew}
                className={`p-2 rounded border focus:ring-2 focus:ring-primary-green outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${getAptitudeSelectStyle(value)}`}
            >
                {APTITUDE_RANKS.map(rank => (
                    <option key={rank} value={rank}>{rank}</option>
                ))}
            </select>
        </div>
    );

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-bold text-gray-800">{t('CharacterAptitudes.title')}</h3>
                {!cardId && !isNew && (
                    <span className="text-sm text-amber-600 bg-amber-50 px-2 py-1 rounded">
                        {t('CharacterAptitudes.select_costume_hint')}
                    </span>
                )}
            </div>

            <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {/* Track Aptitude */}
                    <div className="space-y-4">
                        <h4 className="font-medium text-gray-700 border-b pb-2">{t('CharacterAptitudes.label_track')}</h4>
                        <div className="grid grid-cols-2 gap-4">
                            <AptitudeSelect label={t('CharacterAptitudes.turf')} name="turf" value={formData.turf} />
                            <AptitudeSelect label={t('CharacterAptitudes.dirt')} name="dirt" value={formData.dirt} />
                        </div>
                    </div>

                    {/* Distance Aptitude */}
                    <div className="space-y-4">
                        <h4 className="font-medium text-gray-700 border-b pb-2">{t('CharacterAptitudes.label_distance')}</h4>
                        <div className="grid grid-cols-2 gap-4">
                            <AptitudeSelect label={t('CharacterAptitudes.sprint')} name="short_distance" value={formData.short_distance} />
                            <AptitudeSelect label={t('CharacterAptitudes.mile')} name="mile" value={formData.mile} />
                            <AptitudeSelect label={t('CharacterAptitudes.medium')} name="medium_distance" value={formData.medium_distance} />
                            <AptitudeSelect label={t('CharacterAptitudes.long')} name="long_distance" value={formData.long_distance} />
                        </div>
                    </div>

                    {/* Running Style */}
                    <div className="space-y-4">
                        <h4 className="font-medium text-gray-700 border-b pb-2">{t('CharacterAptitudes.label_style')}</h4>
                        <div className="grid grid-cols-2 gap-4">
                            <AptitudeSelect label={t('CharacterAptitudes.runner')} name="runner" value={formData.runner} />
                            <AptitudeSelect label={t('CharacterAptitudes.leader')} name="leader" value={formData.leader} />
                            <AptitudeSelect label={t('CharacterAptitudes.betweener')} name="betweener" value={formData.betweener} />
                            <AptitudeSelect label={t('CharacterAptitudes.chaser')} name="chaser" value={formData.chaser} />
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="mt-6 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                        {error}
                    </div>
                )}

                {success && (
                    <div className="mt-6 p-3 bg-green-50 text-green-700 rounded-lg text-sm">
                        {t('CharacterAptitudes.success_msg')}
                    </div>
                )}

                {!isNew && (
                    <div className="flex justify-end pt-6">
                        <button
                            type="submit"
                            disabled={loading || !cardId}
                            className={`px-4 py-2 bg-primary-green text-white rounded-lg font-medium hover:bg-green-600 transition-colors ${loading || !cardId ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                            {loading ? t('CharacterAptitudes.button_saving') : t('CharacterAptitudes.button_save')}
                        </button>
                    </div>
                )}
            </form>
        </div>
    );
};

export default CharacterAptitudes;
// Revisado: 2025-12-16
